/**
 * Tenant feature — TanStack Query hooks.
 *
 * This file is the single boundary between the tenants feature and
 * the network. Components never call `apiFetch` directly; they go
 * through `useTenantsQuery`, `useTenantQuery`, `useCreateTenantMutation`,
 * etc.
 *
 * Mutation convention:
 *   - Every mutation is wrapped in `withStepUp()` so the WebAuthn
 *     ceremony runs transparently when the server demands it.
 *   - `onSuccess` always invalidates both the list prefix AND the
 *     specific detail key that changed.
 *   - Mutations do not rely on `optimisticUpdate` — admin mutations
 *     are rare, intentional actions where correctness beats
 *     snappiness.
 */

import { fetchManagedTenantContext } from "@/features/managed-administration/queries";
import { infiniteQueryOptions, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  inviteAdminResponseSchema,
  platformAdminSchema,
  platformAdminsListResponseSchema,
  type AdminRole,
  type InviteAdminResponse,
  type PlatformAdmin,
} from "@/features/platform-admins/schemas";
import {
  slugAvailabilitySchema,
  tenantDetailSchema,
  tenantListItemSchema,
  type TenantListItem,
  tenantOverviewSchema,
  tenantsListResponseSchema,
  type CreateTenantInput,
  type SlugAvailability,
  type TenantDetail,
  type TenantOverview,
  type TenantsListResponse,
  type TenantsListSearch,
  type UpdateTenantInput,
  type UserSource,
} from "./schemas";

// ── Fetchers (exported so route loaders can prefetch without a hook) ─────

export async function fetchTenantsList(
  search: TenantsListSearch,
): Promise<TenantsListResponse> {
  const raw = await apiFetch<unknown>("/api/v1/tenants", {
    searchParams: {
      q: search.q,
      status: search.status,
      customer_audience: search.customer_audience,
      sort_by: search.sortBy,
      sort_order: search.sortOrder,
      offset: search.offset,
    },
  });
  // Parse so bad responses crash the query rather than silently
  // propagate malformed data into the UI.
  return tenantsListResponseSchema.parse(raw);
}

export async function fetchTenantDetail(slug: string): Promise<TenantDetail> {
  const id = /^[0-9a-f-]{36}$/i.test(slug) ? slug : (await fetchManagedTenantContext(slug)).tenant.id;
  const raw = await apiFetch<unknown>(`/api/v1/tenants/${encodeURIComponent(id)}/tenant`);
  return tenantDetailSchema.parse(raw);
}

export const tenantRecordOptions = (slug: string) => queryOptions({
  queryKey: ["tenant-records", slug],
  queryFn: async () => tenantListItemSchema.parse(await apiFetch<unknown>(
    `/api/v1/tenant-records/${encodeURIComponent(slug)}`, { preservePlatformPath: true },
  )),
  staleTime: 30_000,
});

/**
 * A tenant's administrators, as the platform administers them: the same
 * records the tenant's own list returns, read through a platform control
 * (platform_admins.read) that needs no tenant grant. Paged by cursor and read
 * to the end through DataTable's load-more, so the roster is never silently
 * cut short.
 */
export const tenantAdminCensusKey = (tenantID: string) =>
  ["tenant-records", "admin-census", tenantID] as const;

export const tenantAdminCensusOptions = (tenantID: string) => infiniteQueryOptions({
  queryKey: tenantAdminCensusKey(tenantID),
  queryFn: async ({ pageParam }) => platformAdminsListResponseSchema.parse(await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/admin-users/census`,
    { preservePlatformPath: true, searchParams: { limit: 200, cursor: pageParam } },
  )),
  initialPageParam: undefined as string | undefined,
  getNextPageParam: (page) => page.next_cursor,
});

const tenantAdminPath = (tenantID: string, adminID: string, action: string) =>
  `/api/v1/tenants/${encodeURIComponent(tenantID)}/admin-users/${encodeURIComponent(adminID)}/${action}`;

/**
 * One platform lifecycle action on a tenant administrator, run under step-up.
 * The roster is invalidated, never written with the response: the link
 * actions return a one-time enrollment token, which lives in the caller's
 * state only.
 */
function useTenantAdminAction<TVariables, TResult>(
  tenantID: string,
  request: (variables: TVariables) => Promise<TResult>,
) {
  const qc = useQueryClient();
  return useMutation<TResult, ApiError, TVariables>({
    mutationFn: (variables) => withStepUp(() => request(variables)),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: tenantAdminCensusKey(tenantID) }); },
  });
}

const post = (path: string) => apiFetch<unknown>(path, { method: "POST", preservePlatformPath: true });

/** Send a new enrollment link to a pending or accepted administrator (platform_admins.invite). */
export const useResendTenantAdminLinkMutation = (tenantID: string) =>
  useTenantAdminAction<string, InviteAdminResponse>(tenantID, async (adminID) =>
    inviteAdminResponseSchema.parse(await post(tenantAdminPath(tenantID, adminID, "platform-reissue-enrollment"))));

/** Withdraw an invitation nobody has accepted (platform_admins.invite). */
export const useWithdrawTenantAdminInvitationMutation = (tenantID: string) =>
  useTenantAdminAction<string, PlatformAdmin>(tenantID, async (adminID) =>
    platformAdminSchema.parse(await post(tenantAdminPath(tenantID, adminID, "platform-withdraw-invitation"))));

/** Reopen a withdrawn invitation with a new link (platform_admins.invite). */
export const useReopenTenantAdminInvitationMutation = (tenantID: string) =>
  useTenantAdminAction<string, InviteAdminResponse>(tenantID, async (adminID) =>
    inviteAdminResponseSchema.parse(await post(tenantAdminPath(tenantID, adminID, "platform-reopen-invitation"))));

/** Deactivate an administrator who accepted (platform_admins.manage). */
export const useDeactivateTenantAdminMutation = (tenantID: string) =>
  useTenantAdminAction<string, PlatformAdmin>(tenantID, async (adminID) =>
    platformAdminSchema.parse(await post(tenantAdminPath(tenantID, adminID, "platform-deactivate"))));

/** Reactivate a deactivated administrator who had accepted (platform_admins.manage). */
export const useReactivateTenantAdminMutation = (tenantID: string) =>
  useTenantAdminAction<string, PlatformAdmin>(tenantID, async (adminID) =>
    platformAdminSchema.parse(await post(tenantAdminPath(tenantID, adminID, "platform-reactivate"))));

/** Change an active administrator's tenant role (platform_admins.manage). */
export const useChangeTenantAdminRoleMutation = (tenantID: string) =>
  useTenantAdminAction<{ adminID: string; role: AdminRole }, PlatformAdmin>(tenantID, async ({ adminID, role }) =>
    platformAdminSchema.parse(await apiFetch<unknown>(tenantAdminPath(tenantID, adminID, "platform-role"), {
      method: "PATCH", body: { role }, preservePlatformPath: true,
    })));

// ── Hooks ────────────────────────────────────────────────────────────────

/**
 * The tenant's own record, including the identity-matching switches.
 *
 * Shares the settings route's cache key, so a console that has been to Settings
 * already holds it. Read by surfaces that must describe what a switch means
 * where it applies rather than where it is set — the name identifier
 * disclosure, whose sentence changes entirely depending on whether the tenant
 * holds email addresses and employee numbers to one person.
 */
export function useTenantDetailQuery(slug: string) {
  return useQuery({
    queryKey: queryKeys.tenants.detail(slug),
    queryFn: () => fetchTenantDetail(slug),
    staleTime: 60_000,
    enabled: slug.length > 0,
  });
}

export function useTenantsQuery(
  search: TenantsListSearch,
  options: { enabled?: boolean } = {},
) {
  return useQuery<TenantsListResponse, ApiError>({
    queryKey: queryKeys.tenants.list(search),
    queryFn: () => fetchTenantsList(search),
    enabled: options.enabled ?? true,
  });
}

// ── Slug availability ────────────────────────────────────────────────────
//
// Backed by GET /api/v1/tenants/check-slug?slug=… . Returns
// {available, reason?} where reason is "taken" | "reserved" |
// "invalid_format" when available=false. 400s when `slug` is empty,
// which is why we gate the hook with `enabled: slug.length > 0`.

export async function fetchSlugAvailability(
  slug: string,
): Promise<SlugAvailability> {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/check-slug?slug=${encodeURIComponent(slug)}`,
  );
  return slugAvailabilitySchema.parse(raw);
}

export function useSlugAvailabilityQuery(slug: string) {
  return useQuery<SlugAvailability, ApiError>({
    queryKey: ["tenants", "slug-availability", slug] as const,
    queryFn: () => fetchSlugAvailability(slug),
    enabled: slug.length > 0,
    // Availability is cheap to fetch but stable over short windows;
    // a small stale time absorbs rapid keystroke debouncing without
    // hammering the server on every re-render.
    staleTime: 10_000,
    retry: false,
  });
}

export function useTenantQuery(slug: string, enabled = true) {
  return useQuery<TenantDetail, ApiError>({
    queryKey: queryKeys.tenants.detail(slug),
    queryFn: () => fetchTenantDetail(slug),
    enabled: slug.length > 0 && enabled,
  });
}

// ── Overview ─────────────────────────────────────────────────────────────

export async function fetchTenantOverview(tenantID: string): Promise<TenantOverview> {
  const raw = await apiFetch<unknown>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/overview`,
  );
  return tenantOverviewSchema.parse(raw);
}

export function useTenantOverviewQuery(tenantID: string) {
  return useQuery<TenantOverview, ApiError>({
    queryKey: queryKeys.tenants.overview(tenantID),
    queryFn: () => fetchTenantOverview(tenantID),
    enabled: tenantID.length > 0,
    // Match the platform overview cadence: server-side rollup is
    // not cached below 60s, and a five-minute refetch interval
    // catches most operator state changes without hammering.
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────

// Derives the three "implied" columns from the operator's explicit
// user_source pick. The backend validates these cross-fields (see
// tenant.go:validateCrossFields), so we have to send a consistent set
// — picking them here keeps the form surface minimal.
function deriveSourceSettings(source: UserSource): {
  identity_source: "builtin" | "external";
  platform_role: "full_idp";
} {
  // platform_role is always full_idp now — every shipping
  // archetype runs PureID as the front-door IdP. The vestigial
  // auth_edge value was removed end-to-end (schema CHECK,
  // backend enum, frontend type) so the data store always
  // reflects runtime behaviour.
  switch (source) {
    case "complete_platform":
      return { identity_source: "builtin", platform_role: "full_idp" };
    case "existing_idp":
      return { identity_source: "external", platform_role: "full_idp" };
    case "enterprise_fed":
      return { identity_source: "external", platform_role: "full_idp" };
  }
}

export function buildCreateTenantRequest(input: CreateTenantInput) {
  const derived = deriveSourceSettings(input.user_source);
  return {
    name: input.name,
    slug: input.slug,
    tenant_type: input.tenant_type,
    user_source: input.user_source,
    identity_source: derived.identity_source,
    platform_role: derived.platform_role,
    ...(input.customer_audience
      ? { customer_audience: input.customer_audience }
      : {}),
    organizations_enabled:
      input.customer_audience === "individual_customers"
        ? true
        : (input.organizations_enabled ?? false),
    deployment_goal: input.deployment_goal,
    ...(input.external_provider
      ? { external_provider: input.external_provider }
      : {}),
  };
}

export function useCreateTenantMutation() {
  const qc = useQueryClient();
  return useMutation<TenantDetail, ApiError, CreateTenantInput>({
    mutationFn: (input) => {
      const body = buildCreateTenantRequest(input);
      return withStepUp(() =>
        apiFetch<unknown>("/api/v1/tenants", {
          method: "POST",
          body,
        }).then((raw) => tenantDetailSchema.parse(raw)),
      );
    },
    onSuccess: (created) => {
      // Seed the detail cache with the authoritative record. This is not
      // just an optimization: the layout route resolves slug → tenant UUID
      // through this exact key via ensureQueryData, which returns cached
      // data WITHOUT revalidating even when stale. A slug can legitimately
      // map to a new UUID (delete + recreate, or a wiped dev database), and
      // a surviving entry would bind every child route to a dead tenant id
      // — the tenant appears in the list but its overview 404s. Overwriting
      // the entry here makes that state unreachable for the creating tab.
      qc.setQueryData(queryKeys.tenants.detail(created.slug), created);
      // Every list view is invalidated — we don't know which filter
      // the user is currently viewing. Cheap, broad, correct.
      qc.invalidateQueries({ queryKey: queryKeys.tenants.all });
    },
  });
}

export function useUpdateTenantMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<TenantDetail, ApiError, UpdateTenantInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`/api/v1/tenants/${encodeURIComponent(slug)}`, {
          method: "PATCH",
          body: input,
        }).then((raw) => tenantDetailSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      // Surgically refresh the detail cache with the new record,
      // then invalidate the list prefix so filters re-derive.
      qc.setQueryData(queryKeys.tenants.detail(slug), updated);
      qc.invalidateQueries({ queryKey: queryKeys.tenants.all });
    },
  });
}

export function useDeleteTenantMutation() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (slug) =>
      withStepUp(() =>
        apiFetch<void>(`/api/v1/tenants/${encodeURIComponent(slug)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, slug) => {
      qc.removeQueries({ queryKey: queryKeys.tenants.detail(slug) });
      qc.invalidateQueries({ queryKey: queryKeys.tenants.all });
    },
  });
}

// Suspend + reactivate mutate tenant status via dedicated POST
// endpoints (not PATCH /status) — the backend models them as
// domain actions with distinct audit events (tenant.suspended /
// tenant.reactivated) rather than generic status updates. Both
// are platform-admin only (PermTenantsWrite + step-up).
//
// Lifecycle results contain inventory metadata; invalidate protected detail
// queries so they reload only when the current actor has customer authority.
export function useSuspendTenantMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<TenantListItem, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(slug)}/suspend`,
          { method: "POST" },
        ).then((raw) => tenantListItemSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tenants.all });
    },
  });
}

export function useReactivateTenantMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation<TenantListItem, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(slug)}/reactivate`,
          { method: "POST" },
        ).then((raw) => tenantListItemSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.tenants.all });
    },
  });
}
